import * as React from "react";
import { cn } from "./utils";

function Input({
  className,
  type,
  size = "default",
  ...props
}: Omit<React.ComponentProps<"input">, "size"> & {
  /** Control-height variant — matches Button/Select: sm h-6 · default h-7 ·
   *  lg h-9. Emits `data-size` so the height + horizontal inset resolve from
   *  the base string, killing per-call-site className repaints (PRD T2).
   *
   *  `field` is the odd one out and is named for its ROLE, not its size: the
   *  32px box (30px inside the two 1px borders) a control takes when it is the
   *  VALUE of a labeled field in a reading column — the object peek's property
   *  rows (Matt, 2026-07-21: "the height of all inputs should be 30px + 2 for
   *  the borders"). It is deliberately not called `md`: the t-shirt ladder has
   *  `default` as the app-wide standard at 28px, so an `md` sitting above
   *  `default` would imply the default is small. The three t-shirt sizes are
   *  unchanged — this is an additional tier, not a re-tuning of the ladder, so
   *  no existing control moves. */
  size?: "sm" | "default" | "field" | "lg";
}) {
  return (
    <input
      type={type}
      data-slot="input"
      data-size={size}
      // `leading-none` optically centers the value inside the fixed control
      // height — the uniform centering model Button uses (button.tsx:12),
      // applied to every fixed-height control (docs/design/VERTICAL-RHYTHM-PRD.md
      // D4). Height + px are size-driven (sm h-6 / default h-7 / field h-8 /
      // lg h-9); py-1 is shared.
      //
      // THE CAP-TRIM IS DELIBERATELY GONE (2026-07-21). `text-box: trim-both cap
      // alphabetic` ends the text box AT THE ALPHABETIC BASELINE, and an
      // <input> clips to its box — so every descender in every input in the app
      // was sliced flat: "Engineering" rendered as "Enaineerina", and a typed
      // "pygmy jgq" lost five glyphs' worth of tail. Measured side by side
      // (four treatments, same atom, same page): dropping the trim restores the
      // descenders and the value stays centered — `leading-none` alone was
      // already doing the centering, and `line-height:normal` was NOT the
      // cause. Optical centering is worth a lot; it is not worth mutilating the
      // text it is centering. Button keeps its trim: a button has no
      // overflow clip, so its descenders render fine.
      className={cn(
        "w-full min-w-0 rounded-md border border-foreground/15 bg-background py-1 type-body leading-none text-foreground transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-5 file:border-0 file:bg-transparent file:type-body file:text-foreground placeholder:text-muted-foreground hover:border-foreground/25 focus-visible:border-foreground/40 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:type-body data-[size=sm]:h-6 data-[size=default]:h-7 data-[size=field]:h-8 data-[size=lg]:h-9 data-[size=sm]:px-2.5 data-[size=default]:px-3 data-[size=field]:px-3 data-[size=lg]:px-3.5",
        "aria-invalid:border-destructive",
        className,
      )}
      {...props}
    />
  );
}

export { Input };
